import { useEffect, useRef } from 'react'

import { useQuery } from '@api/hooks'
import { ActionCenterTask, MarketBasketStatus } from '@domain'
import { faListCheck } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useBoolVariation } from '@launchdarkly/react-sdk'
import { Button, ButtonProps, Tooltip } from '@mui/material'
import ActionCenterTasksApi, { ActionCenterKeys } from '@services/actionCenterTasks.api'
import ActionCenter from '@views/MarketBaskets/ActionCenter/ActionCenter'
import { useMarketBasketContext } from '@views/MarketBaskets/MarketBaskets.hooks'
import { useParams } from 'react-router-dom'
import { BooleanParam, StringParam, useQueryParam } from 'use-query-params'

type Props = ButtonProps & {
	iconOnly?: boolean
}

export default function ActionCenterButton({ iconOnly = false, ...rest }: Props) {
	const [actionCenterVisible, setActionCenterVisible] = useQueryParam('action-center', BooleanParam)
	const [purchaseId] = useQueryParam('purchaseId', StringParam)
	const { marketBasketId } = useParams()
	const { marketBasket } = useMarketBasketContext()

	const contractLoadingEnabled = useBoolVariation('feature-art-1113-contract-loading-task', false)

	// Ref to prevent auto-opening the action center multiple times
	const hasAutoOpenedRef = useRef(false)

	const {
		data: tasks = [],
		isRefreshing,
		isFetching,
	} = useQuery({
		queryKey: ActionCenterKeys.tasks(marketBasketId),
		queryFn: () => ActionCenterTasksApi.tasks(marketBasketId),
	})
	const filteredTasks = tasks.filter(
		(task) => contractLoadingEnabled || task.task !== 'CONTRACT_LOADING'
	)

	const skippedTasks = marketBasket?.skippedTasks ?? false
	const status = marketBasket?.status ?? null
	const hasIncompleteTasks = !skippedTasks && filteredTasks.some((t) => !t.completed)

	// Auto-open if there are incomplete tasks
	useEffect(() => {
		if (skippedTasks) return
		// Do not auto open when viewing a specific purchase
		if (purchaseId) return

		if (
			marketBasket?.status === MarketBasketStatus.PROCESSING ||
			marketBasket?.status === MarketBasketStatus.RECALCULATING
		) {
			return
		}

		if (hasIncompleteTasks && !hasAutoOpenedRef.current) {
			setActionCenterVisible(true)
			hasAutoOpenedRef.current = true
		}
	}, [hasIncompleteTasks, marketBasket?.status, purchaseId, setActionCenterVisible, skippedTasks])

	if (!marketBasket) return null

	return (
		<>
			{actionCenterVisible && <ActionCenter onClose={() => setActionCenterVisible(undefined)} />}

			<ActionCenterButtonUI
				loading={isRefreshing || isFetching}
				onClick={() => setActionCenterVisible(true)}
				data={filteredTasks}
				skippedTasks={skippedTasks}
				status={status}
				iconOnly={iconOnly}
				{...rest}
			/>
		</>
	)
}

type PropsUi = ButtonProps & {
	loading: boolean
	data: ActionCenterTask[]
	skippedTasks: boolean
	status: MarketBasketStatus | null
	iconOnly: boolean
}

export function ActionCenterButtonUI({
	loading,
	data,
	skippedTasks,
	onClick,
	status,
	iconOnly,
	...rest
}: PropsUi) {
	const completed = data.filter((t) => t.completed).length
	const total = data.length
	const allComplete = total > 0 && completed === total

	const getButtonColor = () => {
		if (allComplete) return 'success'
		if (skippedTasks) return 'warning'
		return 'primary'
	}

	return (
		<Tooltip title={iconOnly ? `Tasks ${completed}/${total}` : undefined} placement="bottom">
			<span>
				<Button
					loading={
						loading ||
						status === MarketBasketStatus.RECALCULATING ||
						status === MarketBasketStatus.PROCESSING
					}
					loadingPosition="start"
					onClick={onClick}
					startIcon={<FontAwesomeIcon icon={faListCheck} />}
					variant="contained"
					color={getButtonColor()}
					data-testid="basket-action-center"
					sx={
						iconOnly
							? {
									minWidth: 'auto', // Removes the default wide button layout
									padding: '8px', // Centers and cushions the icon evenly
									'& .MuiButton-startIcon': {
										margin: 0, // Removes the right margin from the span
									},
								}
							: undefined
					}
					{...rest}
				>
					{iconOnly ? null : `Tasks ${completed}/${total}`}
				</Button>
			</span>
		</Tooltip>
	)
}
